/* ===================================================================
   OnboardingChecklist -- the ONE derived-completion checklist home (Phase 3,
   2026-07-07, BUILD-SPEC §4 + §13.2). Replaces the linear wizard shell AND the
   separate /first-run screen: the checklist IS onboarding AND the post-signup
   home.

   - Completion is DERIVED FROM REAL ACCOUNT DATA via useSetupProgress (the
     single source of truth), never from "clicked Next".
   - Each item LAUNCHES its existing setup flow (onOpenTask) and returns here.
   - Two mount contexts share this one component:
       variant="full"     -> the full-screen /onboarding home (routes/Onboarding
                             manages the open task in place).
       variant="embedded"  -> overlaid on a returning zero-data dashboard (§9);
                             opening an item navigates to the full-screen home.
   - Mobile-first, one-handed phone. Tokens only; teal done-check, gold star,
     accent blue. Nothing here sends or posts -- launching a task is navigation.
   =================================================================== */
import { useState } from 'react';
import { BrandMark } from './BrandMark';
import { useMe, useSetupProgress } from '../hooks';
import type { SetupStep, SetupStepId } from '../hooks';
import type { OnboardingTaskId } from '../lib/onboardingState';
import '../styles/onboarding-checklist.css';

/* Each tracked checklist item maps to the setup task it launches. */
const TASK_FOR_STEP: Record<SetupStepId, OnboardingTaskId> = {
  diagnostic: 'diagnostic',
  gbp: 'connect',
  import: 'customers',
  voice: 'voice',
  reactivation: 'reactivation',
  requests: 'firstask',
  widget: 'widget',
};

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
      <path d="M5 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function ChevIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export interface OnboardingChecklistProps {
  variant?: 'full' | 'embedded';
  /* Launch a tracked item's setup flow (the diagnostic is one of these -- it is
     CORE step 1, launched via onOpenTask('diagnostic')). */
  onOpenTask: (task: OnboardingTaskId) => void;
  /* Full variant only: leave the checklist for the dashboard. */
  onGoToDashboard?: () => void;
  /* Full variant only: the top "skip setup" affordance. */
  onSkipSetup?: () => void;
}

export function OnboardingChecklist({
  variant = 'full',
  onOpenTask,
  onGoToDashboard,
  onSkipSetup,
}: OnboardingChecklistProps) {
  const { data: me } = useMe();
  const progress = useSetupProgress();
  // "Skip for now" is a per-session visual de-emphasis. It NEVER marks an item
  // done -- the row stays incomplete until the real data condition is met.
  const [skipped, setSkipped] = useState<Set<SetupStepId>>(new Set());

  const embedded = variant === 'embedded';
  const firstName = (me?.business_name ?? '').trim();

  // Loading: never flash a fresh checklist at an already-configured account.
  if (progress.loading) {
    return (
      <div className={'oc-home' + (embedded ? ' oc-home--embedded' : '')}>
        <div className="oc-sk oc-sk--bar" />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="oc-sk oc-sk--row" />
        ))}
      </div>
    );
  }

  const { steps, doneCount, total, pct, coreComplete, nextStep } = progress;
  // The MAIN checklist shows ONLY the four CORE steps (diagnostic, connect+
  // confirm Google, add customers, first ask) -> "X of 4". Brand voice is
  // assumed (never a row); reactivation is a post-activation dashboard push;
  // the widget is an at-leisure extra shown only once core is complete.
  const coreSteps = steps.filter((s) => s.core);

  function toggleSkip(id: SetupStepId) {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function renderItem(step: SetupStep, index: number) {
    const num = index + 1;
    const isNext = !step.done && nextStep?.id === step.id;
    const isSkipped = skipped.has(step.id);
    const cls =
      'oc-item' +
      (step.done ? ' oc-item--done' : '') +
      (!step.done && step.emphasis === 'primary' ? ' oc-item--primary' : '') +
      (!step.done && step.emphasis === 'muted' ? ' oc-item--muted' : '');

    return (
      <div key={step.id}>
        <button
          type="button"
          className={cls}
          onClick={() => onOpenTask(TASK_FOR_STEP[step.id])}
          aria-label={`${step.title}${step.done ? ' (done)' : ''}`}
        >
          <span
            className={
              'oc-mark' +
              (step.state === 'done'
                ? ' oc-mark--done'
                : step.state === 'in_progress'
                  ? ' oc-mark--in_progress'
                  : '')
            }
            aria-hidden="true"
          >
            {step.done ? <CheckIcon /> : num}
          </span>

          <span className="oc-item__body">
            <span className="oc-item__titlerow">
              <span className="oc-item__title">{step.title}</span>
              {isNext ? (
                <span className="oc-pill oc-pill--next">Do this next</span>
              ) : step.state === 'in_progress' ? (
                <span className="oc-pill oc-pill--progress">In progress</span>
              ) : !step.done && step.id === 'diagnostic' ? (
                <span className="oc-pill oc-pill--optional">20 sec</span>
              ) : !step.done && step.id === 'widget' ? (
                <span className="oc-pill oc-pill--later">Later</span>
              ) : !step.done && step.optional ? (
                <span className="oc-pill oc-pill--optional">Optional</span>
              ) : null}
            </span>
            <span className="oc-item__why">{step.desc}</span>
          </span>

          <span className="oc-item__aside">
            {step.done ? (
              <span className="oc-item__done-lab">Done</span>
            ) : (
              <span className="oc-item__chev" aria-hidden="true">
                <ChevIcon />
              </span>
            )}
          </span>
        </button>

        {/* Skip-for-now: only for incomplete, non-primary items (the activation
            path is never encouraged to skip). Does NOT mark the item done. */}
        {!step.done && step.emphasis !== 'primary' && (
          <div className="oc-skiprow">
            {isSkipped ? (
              <span className="oc-skipped-note">
                Skipped for now &mdash; still to do.{' '}
                <button className="oc-skip" onClick={() => toggleSkip(step.id)}>
                  Undo
                </button>
              </span>
            ) : (
              <button className="oc-skip" onClick={() => toggleSkip(step.id)}>
                Skip for now
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // After core is complete we collapse to "you're all set" and offer the
  // at-leisure widget as the one optional extra. Brand voice (assumed) and
  // reactivation (a post-activation push) are deliberately NOT listed here.
  const optionalExtras = steps.filter((s) => s.id === 'widget' && !s.done);

  const header = (
    <>
      {!embedded && (
        <header className="oc-top">
          <BrandMark size={30} />
          {onSkipSetup && (
            <button className="oc-top__skip" onClick={onSkipSetup}>
              Skip setup
            </button>
          )}
        </header>
      )}

      <div className="oc-head">
        <div className="oc-head__eyebrow">Your setup</div>
        <h1 className="oc-head__h">
          {coreComplete
            ? firstName
              ? `${firstName} is set up`
              : "You're set up"
            : firstName
              ? `Let's finish setting up ${firstName}`
              : "Let's get you set up"}
        </h1>
        <p className="oc-head__p">
          {coreComplete
            ? 'Your review engine is live. Rhody Reviews asks, drafts, and monitors. You approve before anything posts.'
            : 'A few quick steps and your first 5-stars start coming in. Tap any item to start. Nothing is sent until you send it.'}
        </p>
      </div>

      <div className="oc-progress">
        <div className="oc-progress__track">
          <div className="oc-progress__fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="oc-progress__count">
          {doneCount} of {total} done
        </span>
      </div>
    </>
  );

  return (
    <div className={'oc-home' + (embedded ? ' oc-home--embedded' : '')}>
      {header}

      {/* (Removed the redundant "Do this next" masthead -- it duplicated Step 1
         below and read as a confusing extra heading. Steve 2026-07-08.) */}

      {coreComplete ? (
        <>
          <div className="oc-done">
            <div className="oc-done__badge" aria-hidden="true">
              <CheckIcon />
            </div>
            <div className="oc-done__h">You&apos;re all set</div>
            <p className="oc-done__p">
              {embedded
                ? 'Your first reviews will show up here as they arrive. Keep sending requests to keep them flowing.'
                : 'Rhody Reviews is working for you. Head to your dashboard to watch the reviews roll in.'}
            </p>
            {!embedded && onGoToDashboard && (
              <button
                className="btn btn--primary btn--block oc-done__cta"
                onClick={onGoToDashboard}
              >
                Go to dashboard
              </button>
            )}
          </div>

          {optionalExtras.length > 0 && (
            <div className="oc-extras">
              <div className="oc-extras__lab">Optional extras</div>
              <div className="oc-list">
                {optionalExtras.map((s) =>
                  renderItem(
                    s,
                    steps.findIndex((x) => x.id === s.id),
                  ),
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="oc-list">
          {/* The slim linear chute as a resume home: exactly the four CORE steps
              (diagnostic, connect+confirm Google, add customers, first ask) ->
              "X of 4". Brand voice is assumed (never a row); reactivation is a
              post-activation dashboard push; the widget is an at-leisure extra. */}
          {coreSteps.map((s, i) => renderItem(s, i))}
        </div>
      )}
    </div>
  );
}

export default OnboardingChecklist;
